import React, { useMemo, useState, useEffect } from 'react';
import styled from 'styled-components';
import { resolveImage, getCORSFetchUrl } from '../../../Utils/helper';
import { useAppSelector } from '../../../Redux/Hooks';
import { IMAGE_CDN } from '../../../config/config';

const CREAM = '#FAF6E9';
const MAROON = '#5D1C34';
const GOLD = '#B58E58';
const MUTED = '#8B7355';
const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "'Urbanist', 'Helvetica', sans-serif";

export interface ReportColumn {
  key: string;
  label: string;
  align?: 'left' | 'center' | 'right';
  width?: string;
  render?: (value: unknown, row: Record<string, unknown>) => React.ReactNode;
}

export interface ReportSummaryItem {
  label: string;
  value: string;
  highlight?: boolean;
}

interface ListReportPrintTemplateProps {
  printRef: React.RefObject<HTMLDivElement>;
  title: string;
  subtitle?: string;
  user?: { name?: string; phone_number?: string; email?: string; studio_logo?: string };
  studioLogo?: string | null;
  studioName?: string | null;
  studioAddress?: string | null;
  generatedDate?: string;
  columns: ReportColumn[];
  rows: Record<string, unknown>[];
  summaryItems?: ReportSummaryItem[];
  footerText?: string;
}

const PrintRoot = styled.div`
  position: absolute;
  top: -9999px;
  left: -9999px;
`;

const PrintDocument = styled.div`
  width: 790px;
  padding: 48px 56px 40px;
  background: ${CREAM};
  box-sizing: border-box;
  color: ${MAROON};
  font-family: ${SANS};
  position: relative;
  border: 1px solid ${GOLD};
`;

const CornerOrnament = styled.div<{ $position: 'tl' | 'tr' }>`
  position: absolute;
  top: 14px;
  ${props => (props.$position === 'tl' ? 'left: 14px;' : 'right: 14px; transform: scaleX(-1);')}
  width: 48px;
  height: 48px;
  opacity: 0.55;
  pointer-events: none;
  svg {
    width: 100%;
    height: 100%;
  }
`;

const DiamondDivider = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${props => (props.$compact ? '18px 0' : '22px 0 28px')};
`;

const DiamondLine = styled.div`
  flex: 1;
  height: 1px;
  background: ${GOLD};
`;

const Diamond = styled.span`
  display: block;
  width: 7px;
  height: 7px;
  background: ${GOLD};
  transform: rotate(45deg);
  margin: 0 10px;
  flex-shrink: 0;
`;

const HeaderBlock = styled.div`
  text-align: center;
  padding-top: 8px;
`;

const BrandLogo = styled.img`
  height: 48px;
  max-width: 200px;
  width: auto;
  object-fit: contain;
  margin-bottom: 10px;
`;

const BrandName = styled.h1`
  font-family: ${SERIF};
  font-size: 26px;
  font-weight: 700;
  margin: 0;
  color: ${MAROON};
`;

const StudioName = styled.p`
  font-family: ${SERIF};
  font-size: 14px;
  font-style: italic;
  margin: 4px 0 0;
  color: ${MAROON};
`;

const StudioAddressLine = styled.p`
  font-size: 11px;
  color: ${MUTED};
  margin: 4px 0 0;
`;

const ReportTitle = styled.h2`
  font-family: ${SERIF};
  font-size: 18px;
  font-style: italic;
  font-weight: 400;
  margin: 18px 0 0;
  color: ${MAROON};
`;

const ReportSubtitle = styled.p`
  font-size: 11px;
  color: ${MUTED};
  margin: 6px 0 0;
`;

const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;
  font-size: 11px;
  color: ${MUTED};
`;

const MetaItem = styled.span`
  strong {
    color: ${MAROON};
    font-weight: 700;
  }
`;

const SectionTitle = styled.h3`
  font-family: ${SERIF};
  font-size: 15px;
  font-style: italic;
  font-weight: 400;
  color: ${MAROON};
  margin: 0 0 12px;
`;

const PrintTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeadRow = styled.tr`
  border-bottom: 1px solid ${MAROON};
`;

const Th = styled.th<{ $align?: string; $width?: string }>`
  padding: 0 6px 8px 0;
  text-align: ${props => props.$align || 'left'};
  font-size: 10px;
  font-weight: 700;
  color: ${MAROON};
  width: ${props => props.$width || 'auto'};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const BodyRow = styled.tr<{ $even?: boolean }>`
  background: ${props => (props.$even ? 'rgba(181, 142, 88, 0.06)' : 'transparent')};
  border-bottom: 1px solid rgba(181, 142, 88, 0.2);

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td<{ $align?: string }>`
  padding: 10px 6px 10px 0;
  font-size: 11px;
  color: ${MAROON};
  text-align: ${props => props.$align || 'left'};
  vertical-align: middle;
  line-height: 1.35;
`;

const CellBold = styled.span`
  font-weight: 700;
`;

const EmptyRow = styled.tr``;

const EmptyCell = styled.td`
  padding: 24px 0;
  text-align: center;
  font-size: 12px;
  color: ${MUTED};
  font-style: italic;
`;

const SummaryBlock = styled.div`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
`;

const SummaryInner = styled.div`
  min-width: 280px;
`;

const SummaryLine = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px dashed rgba(181, 142, 88, 0.35);

  &:last-of-type {
    border-bottom: none;
  }
`;

const SummaryKey = styled.span`
  color: ${MUTED};
  font-weight: 500;
`;

const SummaryVal = styled.span`
  font-weight: 700;
  color: ${MAROON};
`;

const SummaryHighlight = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding: 12px 16px;
  background: ${MAROON};
`;

const SummaryHighlightKey = styled.span`
  font-family: ${SERIF};
  font-size: 13px;
  font-style: italic;
  color: ${CREAM};
`;

const SummaryHighlightVal = styled.span`
  font-family: ${SERIF};
  font-size: 16px;
  font-weight: 700;
  color: ${GOLD};
`;

const DocFooter = styled.div`
  margin-top: 28px;
`;

const FooterText = styled.p`
  font-family: ${SERIF};
  font-size: 12px;
  font-style: italic;
  color: ${MAROON};
  text-align: center;
  margin: 0;
  opacity: 0.85;
`;

const CornerSvg = () => (
  <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4 C4 4 4 20 20 4" stroke={GOLD} strokeWidth="0.8" fill="none" />
    <path d="M8 8 C8 8 8 16 16 8" stroke={GOLD} strokeWidth="0.6" fill="none" />
    <circle cx="22" cy="6" r="2" fill={GOLD} opacity="0.6" />
    <path d="M2 12 Q6 8 10 12" stroke={GOLD} strokeWidth="0.5" fill="none" />
  </svg>
);

export const ReportStatusBadge = styled.span<{ $bg: string; $color: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 9px;
  font-weight: 700;
  white-space: nowrap;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

export const ListReportPrintTemplate: React.FC<ListReportPrintTemplateProps> = ({
  printRef,
  title,
  subtitle,
  user,
  studioLogo,
  studioName,
  studioAddress,
  generatedDate,
  columns,
  rows,
  summaryItems = [],
  footerText = 'With gratitude, for choosing KwikMemories',
}) => {
  const { studio_logo: authStudioLogo, business: authBusiness, user: authUser } = useAppSelector(
    (state) => state.auth
  );

  const effectiveUser = user || authUser;

  const effectiveStudioName =
    studioName ||
    (effectiveUser as any)?.studio_name ||
    (effectiveUser as any)?.business?.studio_name ||
    authBusiness?.studio_name ||
    null;

  const effectiveStudioAddress =
    studioAddress ||
    (effectiveUser as any)?.address ||
    (effectiveUser as any)?.business?.address ||
    authBusiness?.address ||
    null;

  const rawLogo =
    studioLogo ||
    (effectiveUser as any)?.studio_logo ||
    (effectiveUser as any)?.business?.studio_logo ||
    authStudioLogo ||
    authBusiness?.studio_logo ||
    null;

  const resolvedLogoUrl = useMemo(() => {
    if (!rawLogo || typeof rawLogo !== 'string' || !rawLogo.trim()) return null;
    return resolveImage(rawLogo) || (rawLogo ? `${IMAGE_CDN}/${rawLogo.replace(/^\//, '')}` : null);
  }, [rawLogo]);

  const [logoSrc, setLogoSrc] = useState<string | null>(null);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    if (!resolvedLogoUrl) {
      setLogoSrc(null);
      setLogoFailed(false);
      return;
    }

    let isMounted = true;
    setLogoFailed(false);

    const convertToDataUrl = async (url: string) => {
      try {
        const fetchUrl = getCORSFetchUrl(url);
        const response = await fetch(fetchUrl, { mode: 'cors' });
        if (!response.ok) throw new Error('Network response not ok');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          if (isMounted && typeof reader.result === 'string') {
            setLogoSrc(reader.result);
          }
        };
        reader.readAsDataURL(blob);
      } catch {
        if (isMounted) {
          setLogoSrc(getCORSFetchUrl(url));
        }
      }
    };

    convertToDataUrl(resolvedLogoUrl);

    return () => {
      isMounted = false;
    };
  }, [resolvedLogoUrl]);

  const dateStr = generatedDate || new Date().toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const highlightItem = summaryItems.find(item => item.highlight);
  const regularSummary = summaryItems.filter(item => !item.highlight);

  return (
    <PrintRoot>
      <PrintDocument ref={printRef}>
        <CornerOrnament $position="tl"><CornerSvg /></CornerOrnament>
        <CornerOrnament $position="tr"><CornerSvg /></CornerOrnament>

        <HeaderBlock>
          {resolvedLogoUrl && !logoFailed && (
            <BrandLogo
              src={logoSrc || getCORSFetchUrl(resolvedLogoUrl)}
              alt={effectiveStudioName || effectiveUser?.name || 'Studio Logo'}
              crossOrigin="anonymous"
              onError={() => setLogoFailed(true)}
            />
          )}
          {effectiveStudioName && <BrandName>{effectiveStudioName}</BrandName>}
          {effectiveUser?.name && effectiveUser.name !== effectiveStudioName && (
            <StudioName>{effectiveUser.name}</StudioName>
          )}
          {effectiveStudioAddress && (
            <StudioAddressLine>{effectiveStudioAddress}</StudioAddressLine>
          )}
          <ReportTitle>{title}</ReportTitle>
          {subtitle && <ReportSubtitle>{subtitle}</ReportSubtitle>}
        </HeaderBlock>

        <DiamondDivider>
          <DiamondLine />
          <Diamond />
          <DiamondLine />
        </DiamondDivider>

        <MetaRow>
          <MetaItem>Generated on <strong>{dateStr}</strong></MetaItem>
          <MetaItem><strong>{rows.length}</strong> record{rows.length !== 1 ? 's' : ''}</MetaItem>
        </MetaRow>

        <SectionTitle>Report Details</SectionTitle>
        <PrintTable>
          <thead>
            <TableHeadRow>
              <Th $width="32px">#</Th>
              {columns.map(col => (
                <Th key={col.key} $align={col.align} $width={col.width}>
                  {col.label}
                </Th>
              ))}
            </TableHeadRow>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <EmptyRow>
                <EmptyCell colSpan={columns.length + 1}>No records found</EmptyCell>
              </EmptyRow>
            ) : (
              rows.map((row, idx) => (
                <BodyRow key={idx} $even={idx % 2 === 1}>
                  <Td>{idx + 1}</Td>
                  {columns.map(col => {
                    const value = row[col.key];
                    return (
                      <Td key={col.key} $align={col.align}>
                        {col.render
                          ? col.render(value, row)
                          : value != null && value !== ''
                            ? String(value)
                            : '—'}
                      </Td>
                    );
                  })}
                </BodyRow>
              ))
            )}
          </tbody>
        </PrintTable>

        {summaryItems.length > 0 && (
          <SummaryBlock>
            <SummaryInner>
              {regularSummary.map((item, idx) => (
                <SummaryLine key={idx}>
                  <SummaryKey>{item.label}</SummaryKey>
                  <SummaryVal>{item.value}</SummaryVal>
                </SummaryLine>
              ))}
              {highlightItem && (
                <SummaryHighlight>
                  <SummaryHighlightKey>{highlightItem.label}</SummaryHighlightKey>
                  <SummaryHighlightVal>{highlightItem.value}</SummaryHighlightVal>
                </SummaryHighlight>
              )}
            </SummaryInner>
          </SummaryBlock>
        )}

        <DocFooter>
          <DiamondDivider $compact>
            <DiamondLine />
            <Diamond />
            <DiamondLine />
          </DiamondDivider>
          <FooterText>{footerText}</FooterText>
        </DocFooter>
      </PrintDocument>
    </PrintRoot>
  );
};

export { CellBold };
